import { api } from './api';

// Types
export interface FAQ {
  _id: string;
  question: string;
  answer: string;
  category?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFAQData {
  question: string;
  answer: string;
  category?: string;
  order?: number;
  isActive?: boolean;
}

export interface UpdateFAQData {
  question?: string;
  answer?: string;
  category?: string;
  order?: number;
  isActive?: boolean;
}

export interface GetFAQsParams {
  page?: number;
  limit?: number;
  category?: string;
  isActive?: boolean;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GetFAQsResponse {
  faqs: FAQ[];
  pagination: PaginationInfo;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// FAQ Service Class
export class FAQService {
  private static instance: FAQService;

  public static getInstance(): FAQService {
    if (!FAQService.instance) {
      FAQService.instance = new FAQService();
    }
    return FAQService.instance;
  }

  // Get FAQs with pagination and filters
  async getFAQs(params: GetFAQsParams = {}): Promise<ApiResponse<GetFAQsResponse | FAQ[]>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

      const response = await api.get(`/faq?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching FAQs:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch FAQs');
    }
  }

  // Get FAQ by ID
  async getFAQById(id: string): Promise<ApiResponse<FAQ>> {
    try {
      const response = await api.get(`/faq/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching FAQ:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch FAQ');
    }
  }

  // Create FAQ
  async createFAQ(faqData: CreateFAQData): Promise<ApiResponse<FAQ>> {
    try {
      const response = await api.post('/faq', faqData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating FAQ:', error);
      throw new Error(error.response?.data?.message || 'Failed to create FAQ');
    }
  }

  // Update FAQ
  async updateFAQ(id: string, faqData: UpdateFAQData): Promise<ApiResponse<FAQ>> {
    try {
      const response = await api.put(`/faq/${id}`, faqData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating FAQ:', error);
      throw new Error(error.response?.data?.message || 'Failed to update FAQ');
    }
  }

  // Delete FAQ
  async deleteFAQ(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/faq/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting FAQ:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete FAQ');
    }
  }

  // Toggle FAQ status (activate/deactivate)
  async toggleFAQStatus(id: string, isActive: boolean): Promise<ApiResponse<FAQ>> {
    try {
      const response = await api.put(`/faq/${id}`, { isActive });
      return response.data;
    } catch (error: any) {
      console.error('Error toggling FAQ status:', error);
      throw new Error(error.response?.data?.message || 'Failed to toggle FAQ status');
    }
  }
}

// Export singleton instance
export const faqService = FAQService.getInstance();




